import { MenuItem, TableCellProps, Tooltip } from "@mui/material";
import moment from "moment";
import React, { useState } from "react";
import BarekamText from "../../../../../pages/components/text";
import BarekamCheckbox from "../../../../../pages/components/checkbox";
import { PaymentInfoModel } from "../../../../shared/models/order/payment-info.model";
import { ShippingInfoModel } from "../../../../shared/models/order/shipping-info.model";
import { PaymentStatusModel } from "../../../../shared/models/order/payment-status.model";
import { useAppDispatch, useAppSelector } from "../../../../../redux/store";
import { Recipient } from "../../../../shared/models/user/recipient/recipient";
import { OrderModel } from "../../../../shared/models/order/order-item.model";
import { changeOrderSelection } from "../../../../../redux/slices/orders-slice";
import { TrackingRecord } from "../../../../shared/models/order/trackingRecord.model";
import {
  Message,
  MessageSections,
} from "../../../../components/message/message";
import { ClickableColumnContainer, OrderTableCell } from "../styled";
import useMeasure from "react-use-measure";
import { InfoOutlined } from "@mui/icons-material";
import BarekamButton, { SimpleButtons } from "../../../../components/button";
import ordersService from "../../../../../services/orders.service";
import { ConfirmationDialog } from "../../../../components/dialogs/confirmation-dialog";
import { useNavigate } from "react-router-dom";
import BarekamChip from "../../../../components/chip/barekamChip";
import { Product } from "../../../../shared/models/order/product.model";
import { ThreeDot } from "../../../../components/three-dot/three-dot";
import { AdditionalServiceModel } from "../../../../shared/models/order/additional-service.model";
import { BulkActionActions } from "./blocks/bulk-action-actions";
import {
  BarekamColumns,
  WarehouseWorkerColumns,
} from "../../models/column-models";
import { BarekamIcon } from "../../../../components/icon/barekam-icon";
import { OrderIcons } from "../../../../components/icon/variants";
import { WarningDialog } from "../../../../components/dialogs/warning-dialog";
import QRCode from "react-qr-code";
import { OrderStatesEnum } from "../../../enums/order-states.enum";
import { OrderGroupModel } from "../../../../shared/models/order/orderGroupModel";
import { DurationChip } from "../../components/duration-chip/duration-chip";
import { StatusChip } from "../../components/status-chip/duration-chip";
import { RowActions } from "./blocks/row-actions";
import {
  AllOrdersActions,
  OrderHistoryActions,
  RowActionsByState,
} from "../../../../../management-pages/actions/actions-by-state";
import { getMessage } from "../../../../shared/helper-functions";

export const RowCell = ({
  cellVariant,
  field,
  row,
  groups,
  clicked,
  payButtonClicked,
  isName,
  onAction,
  isWarehouseWorker,
  ...props
}: OrderTableCellProps) => {
  const [ref, bounds] =
    cellVariant === "productTrackNumbers" || cellVariant === "sender"
      ? useMeasure()
      : [];
  const [width, setWidth] = useState<number | undefined>(undefined);

  if (cellVariant === "productTrackNumbers" || cellVariant === "sender") {
    if (!width && bounds && bounds.width) {
      setWidth(bounds.width / 10);
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const value = row[field];

  return (
    <OrderTableCell {...props} ref={ref}>
      <CellContent
        cellVariant={cellVariant}
        row={row}
        groups={groups}
        value={value}
        width={width}
        isName={isName}
        clicked={clicked}
        onAction={onAction}
        payButtonClicked={payButtonClicked}
        isWarehouseWorker={isWarehouseWorker}
      />
    </OrderTableCell>
  );
};

const CellContent = ({
  cellVariant,
  value,
  row,
  groups,
  width,
  isName,
  clicked,
  onAction,
  payButtonClicked,
  isWarehouseWorker,
}: OrderTableCellProps) => {
  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );

  const dispatch = useAppDispatch();

  const [openConfirmationPopup, setOpenConfirmationPopup] =
    useState<boolean>(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  const navigate = useNavigate();

  const { lists } = useAppSelector((state) => state.CRMReducer);

  async function deleteOrder() {
    setOpenConfirmationPopup(true);
  }

  async function handleConfirmationDialogClose(confirmed: boolean) {
    setOpenConfirmationPopup(false);
    if (confirmed) {
      if (row.orderId) {
        await ordersService
          .deleteOrderById(row.orderId)
          .catch((error) => console.log(error));
      }
    }
    navigate(0);
  }

  function toggleCheckbox(row: OrderModel) {
    if (row.orderId) {
      dispatch(changeOrderSelection(row.orderId));
    }
  }

  switch (cellVariant) {
    case BarekamColumns.barekamTrack:
      return (
        <ClickableColumnContainer>
          <BarekamButton type={SimpleButtons.LINK} onClick={() => clicked(row)}>
            <BarekamText variant={"m"} bold>
              {value}
            </BarekamText>
          </BarekamButton>

          {isWarehouseWorker && row.isLabelPrinted ? (
            <BarekamIcon variant={OrderIcons.hasPrintedLabel} />
          ) : (
            ""
          )}
        </ClickableColumnContainer>
      );
    case BarekamColumns.checkbox:
      return (
        <BarekamCheckbox
          onClick={() => toggleCheckbox(row)}
          checked={!!value}
        />
      );
    case BarekamColumns.text:
      if (isName) {
        return (
          <ClickableColumnContainer>
            <BarekamButton
              type={SimpleButtons.LINK}
              onClick={() => clicked(row)}
            >
              <BarekamText variant={"m"} bold>
                {value}
              </BarekamText>
            </BarekamButton>
          </ClickableColumnContainer>
        );
      }
      return <BarekamText variant={"m"}>{value}</BarekamText>;
    case BarekamColumns.paymentStatus:
      const paymentStatus = value as PaymentStatusModel;

      return (
        <BarekamChip
          messageKey={paymentStatus.name}
          statusId={paymentStatus.paymentStatusId}
          chipSize={"s"}
        />
      );
    case BarekamColumns.date:
      const isOnHold = row.additionalService?.some(
        (service) => service.additionalServiceId === 6,
      );

      if (isOnHold) {
        return (
          <div style={{ display: "flex", gap: "0.25rem" }}>
            <BarekamText variant={"m"} opaque>
              <Message
                messageKey={"order_pending"}
                sectionId={MessageSections.ORDERS}
              />
            </BarekamText>

            <Tooltip
              title={getMessage(
                "order_pending_hint",
                MessageSections.ORDERS,
                messageCache,
                languageId,
              )}
            >
              <InfoOutlined opacity={0.6} />
            </Tooltip>
          </div>
        );
      } else {
        return (
          <BarekamText variant={"m"}>
            {moment(value as number).format("DD/MM/YYYY")}
          </BarekamText>
        );
      }
    case BarekamColumns.money:
      const price = (value as PaymentInfoModel)?.shippingAmount;
      const shippingDiscount =
        (value as PaymentInfoModel)?.appliedDiscount || 0;
      const currency = (value as PaymentInfoModel)?.shippingAmountCurrency;

      return (
        <BarekamText variant={"m"}>
          {price || shippingDiscount ? (
            price - shippingDiscount + " " + currency?.name[languageId]
          ) : (
            <Message
              messageKey={"hy_order_calculating"}
              sectionId={MessageSections.ORDERS}
            />
          )}
        </BarekamText>
      );
    case BarekamColumns.discount:
      const discount = (value as PaymentInfoModel)?.appliedDiscount || 0;
      const discountCurrency = (value as PaymentInfoModel)
        ?.appliedDiscountCurrency;

      return (
        <BarekamText variant={"m"}>
          {discount ? discount + " " + discountCurrency?.name[languageId] : ""}
        </BarekamText>
      );
    case BarekamColumns.recipient:
      return value ? (
        <BarekamText variant={"m"}>
          {(value as Recipient).firstName} {(value as Recipient).lastName}
        </BarekamText>
      ) : (
        <></>
      );
    case BarekamColumns.warehouse:
      return (
        <BarekamText variant={"m"}>
          {row.shippingInfo?.destinationWarehouse?.city}
        </BarekamText>
      );
    case BarekamColumns.weight:
      return (
        <BarekamText variant={"m"}>
          {row.weight} {row.weightUnit?.name[languageId]}
        </BarekamText>
      );
    case BarekamColumns.price:
      return (
        <BarekamText variant={"m"}>
          {row.paymentInfo?.shippingAmount}{" "}
          {row.paymentInfo?.shippingAmountCurrency?.sign}
        </BarekamText>
      );
    case BarekamColumns.productCount:
      const collectedProductsAmount = row.products.filter(
        (product) => product.isInWarehouse,
      ).length;
      const allProductsAmount = row.products.length;

      return (
        <BarekamText variant={"m"}>
          {collectedProductsAmount}/{allProductsAmount}
        </BarekamText>
      );
    case BarekamColumns.productPrice:
      const productPriceProduct = value && (value as Product[])[0];

      if (productPriceProduct && productPriceProduct.price) {
        return (
          <BarekamText variant={"m"}>
            {`${productPriceProduct.price} ${productPriceProduct.currency?.sign}`}
          </BarekamText>
        );
      } else {
        return <></>;
      }
    case BarekamColumns.productWeight:
      const productWeightProduct = value && (value as Product[])[0];

      if (productWeightProduct && productWeightProduct.weight) {
        return (
          <BarekamText variant={"m"}>
            {`${productWeightProduct.weight} ${productWeightProduct.weightUnit?.name[languageId]}`}
          </BarekamText>
        );
      } else {
        return <></>;
      }
    case BarekamColumns.productTrackNumbers:
      if (width) {
        return (
          <>
            {row.products.map((product) => (
              <BarekamText
                variant={"m"}
                maxWidth={width}
                key={product.trackingNumber}
              >
                {product.trackingNumber}
              </BarekamText>
            ))}
          </>
        );
      } else {
        return <></>;
      }
    case BarekamColumns.firstWarehouse:
      return <>{row.shippingInfo?.warehouse?.name}</>;
    case BarekamColumns.orderDate:
      return <>{moment(row?.dateCreated as number).format("DD/MM/YYYY:HH:mm")}</>;
    case BarekamColumns.trackRecord:
      return <DurationChip trackingRecord={value as TrackingRecord[]} />;
    case BarekamColumns.trackStatus:
      return <StatusChip trackingRecord={value as TrackingRecord[]} />;
    case BarekamColumns.lastActionDate:
      const lastAction = (value as TrackingRecord[])[0];

      if (lastAction) {
        return (
          <BarekamText variant={"m"}>
            {moment(lastAction.enteredDate).format("DD/MM/YYYY HH:mm")}
          </BarekamText>
        );
      } else {
        return <></>;
      }
    case BarekamColumns.deliveredDate:
      if (value as string) {
        return (
          <BarekamText variant={"m"}>
            {moment(value as string).format("DD/MM/YYYY HH:mm")}
          </BarekamText>
        );
      } else {
        return <></>;
      }
    case BarekamColumns.sender:
      if (width) {
        if ((value as ShippingInfoModel).sendBy) {
          return (
            <BarekamText variant={"m"} maxWidth={width}>
              <Message
                messageKey={"send_by_friend"}
                sectionId={MessageSections.ORDERS}
              />
            </BarekamText>
          );
        }
        return (
          <>
            {row.products.map((product) => (
              <BarekamText
                variant={"m"}
                maxWidth={width}
                key={product.trackingNumber}
              >
                {product.store?.name?.[languageId]}
              </BarekamText>
            ))}
          </>
        );
      } else {
        return <></>;
      }
    case BarekamColumns.shippingCategory:
      return (
        <BarekamChip
          messageKey={(value as ShippingInfoModel)?.shippingMethod?.name}
          statusId={3}
          chipSize={"s"}
        />
      );
    case BarekamColumns.actions:
      const orderStateId = row?.trackRecord[0]?.state.orderStateId;
      const orderInEnteredState = orderStateId === 1;

      return (
        <div style={{ width: "100%", display: "flex", justifyContent: "end" }}>
          {!orderInEnteredState && row.paymentStatus?.paymentStatusId !== 2 && (
            <BarekamButton
              type={SimpleButtons.LINK}
              onClick={() => payButtonClicked(row)}
            >
              <BarekamText variant={"m"} bold>
                <Message
                  messageKey={"order_list_pay"}
                  sectionId={MessageSections.ORDERS}
                />
              </BarekamText>
            </BarekamButton>
          )}

          {row.paymentStatus?.paymentStatusId === 2 &&
            orderStateId === OrderStatesEnum.readyForPickup && (
              <BarekamButton
                type={SimpleButtons.LINK}
                onClick={() => setQrDialogOpen(true)}
              >
                <BarekamText variant={"m"} bold>
                  <Message
                    messageKey={"see_qr"}
                    sectionId={MessageSections.ORDERS}
                  />
                </BarekamText>
              </BarekamButton>
            )}

          {orderInEnteredState && (
            <ThreeDot>
              <MenuItem onClick={deleteOrder}>
                <Message
                  messageKey={"delete"}
                  sectionId={MessageSections.ORDERS}
                />
              </MenuItem>
              <MenuItem onClick={() => clicked(row)}>
                <Message
                  messageKey={"edit"}
                  sectionId={MessageSections.ORDERS}
                />
              </MenuItem>
            </ThreeDot>
          )}

          {openConfirmationPopup && (
            <ConfirmationDialog
              open={openConfirmationPopup}
              handleClose={handleConfirmationDialogClose}
              titleKey={"delete_order"}
              descriptionKey={"delete_confirmation_description"}
              checkboxTitle={"save_card_info"}
            />
          )}

          <WarningDialog
            open={qrDialogOpen}
            handleClose={() => {
              setQrDialogOpen(false);
            }}
            titleKey={"qr_dialog"}
            descriptionIcon={
              <QRCode
                style={{ height: "6rem", width: "6rem" }}
                value={row.internalTrackingNumber}
              />
            }
            descriptionKey={"qr_dialog_description"}
          />
        </div>
      );
    case BarekamColumns.additionalServices:
      return (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}
        >
          {(value as AdditionalServiceModel[]).map((service) => (
            <BarekamChip
              messageKey={service.name}
              statusId={5}
              chipSize={"s"}
            />
          ))}
        </div>
      );
    case WarehouseWorkerColumns.inWarehouseActions:
      return (
        <RowActions
          row={row}
          onAction={onAction}
          actions={RowActionsByState[OrderStatesEnum.inWarehouse]}
        />
      );
    case WarehouseWorkerColumns.readyForShipmentActions:
      return (
        <RowActions
          row={row}
          onAction={onAction}
          actions={RowActionsByState[OrderStatesEnum.readyForShipment]}
        />
      );
    case WarehouseWorkerColumns.onTheWayActions:
      return (
        <RowActions
          row={row}
          onAction={onAction}
          actions={RowActionsByState[OrderStatesEnum.onTheWay]}
        />
      );
    case WarehouseWorkerColumns.unidentifiedActions:
      return (
        <RowActions
          row={row}
          onAction={onAction}
          actions={RowActionsByState[OrderStatesEnum.unidentified]}
        />
      );
    case WarehouseWorkerColumns.readyForPickupActions:
      return (
        <RowActions
          row={row}
          onAction={onAction}
          actions={RowActionsByState[OrderStatesEnum.readyForPickup]}
        />
      );
    case WarehouseWorkerColumns.inLocalWarehouseActions:
      return (
        <RowActions
          row={row}
          onAction={onAction}
          actions={RowActionsByState[OrderStatesEnum.inLocalWarehouse]}
        />
      );
    case WarehouseWorkerColumns.allOrdersActions:
      if (row.paymentStatus?.paymentStatusId === 2) {
        return (
          <RowActions
            row={row}
            onAction={onAction}
            actions={OrderHistoryActions}
          />
        );
      } else {
        return (
          <RowActions
            row={row}
            onAction={onAction}
            actions={AllOrdersActions}
          />
        );
      }
    case WarehouseWorkerColumns.bulkActionPopupActions:
      return <BulkActionActions row={row} onAction={onAction} />;
    case WarehouseWorkerColumns.location:
      return (
        <>
          {row.depot
            ? [row.depot, row.sector, row.corridor, row.rack, row.bin].join(
              " / ",
            )
            : ""}
        </>
      );
    case WarehouseWorkerColumns.list:
      const orderList = lists.find((list) => list.id == row.crmListId);
      return <>{orderList?.name}</>;
    case WarehouseWorkerColumns.group:
      const orderGroup = groups.find((group) =>
        group.orders.find((order) => order.orderId === row.orderId),
      );
      return <>{orderGroup?.name}</>;
  }
  return <>NO DATA</>;
};

interface OrderTableCellProps extends TableCellProps {
  cellVariant: BarekamColumns | WarehouseWorkerColumns;
  value?:
  | string
  | number
  | boolean
  | ShippingInfoModel
  | PaymentInfoModel
  | PaymentStatusModel
  | Recipient
  | Product[]
  | TrackingRecord[]
  | AdditionalServiceModel[]
  | null
  | undefined;
  field?: string;
  isWarehouseWorker?: boolean;
  isName?: boolean;
  row: OrderModel;
  groups: OrderGroupModel[];
  width?: any;
  clicked: (order: OrderModel) => void;
  payButtonClicked: (order: OrderModel) => void;
  onAction?: (row: OrderModel, action: any) => void;
}
